import PharmacyStyle from '../doctors/Doctors.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { Button, Card, IconButton } from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import PharmacyDialog from './PharmacyDialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { deletePharmacyShop, getPharmacyShops } from '../../apis/pharmacyShopSlice';
import DeleteDialog from '../../components/DeleteDialog';
import CommonAgGrid from '../../components/CommonAgGrid/CommonAgGrid';

export default function PharmacyShop({ search }) {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [operationMode, setOperationMode] = useState('Add');

  // delete dialog state --------------------------------------------------
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);


  const { shops, loading, totalCount } = useSelector((state) => state.pharmacyShopData || {});

  async function callApi() {
    dispatch(getPharmacyShops({ page, pageSize, search: search || '' }));
  }

  useEffect(() => {
    callApi();
  }, [page, pageSize, dispatch, search]);

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const handleEdit = (data) => {
    setOpen(true);
    setEditData(data);
    setOperationMode('Edit');
  };

  // open the confirmation dialog rather than deleting immediately
  const handleDeleteClick = (data) => {
    setDeleteRow(data);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow) return;

    const response = await dispatch(deletePharmacyShop(deleteRow._id));
    if (response?.payload?.data) {
      toast.success(response?.payload.message);
      callApi();
    } else {
      toast.error('Error deleting shop');
    }

    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const handleCancelDelete = () => {
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const columns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: 'Action',
        width: 120,
        sortable: false,
        filterable: false,
        cellRenderer: (params) => (
          <div>
            <IconButton size="small" onClick={() => handleEdit(params.data)} color="primary" aria-label="edit">
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleDeleteClick(params.data)} color="error" aria-label="delete">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ),
      },
      {
        field: 'clinicName',
        headerName: 'Clinic Name',
        minWidth: 180,
        valueGetter: (params) => params.data?.clinicId?.name || '-',
      },
      { field: 'shopName', headerName: 'Shop Name', minWidth: 170 },
      { field: 'ownerName', headerName: 'Owner', minWidth: 140 },
      { field: 'email', headerName: 'Email', minWidth: 190 },
      { field: 'phone', headerName: 'Phone', minWidth: 100 },
      { field: 'address', headerName: 'Address', minWidth: 220 },
    ],
    [],
  );

  return (
    <div className={PharmacyStyle.mainDataTable}>
      {/* global confirmation dialog used for deletes */}
      <DeleteDialog
        open={deleteOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      <Card className={PharmacyStyle.tableCard}>
        <div className={PharmacyStyle.tableHeader}>
          <h2 className={PharmacyStyle.tableTitle}>Pharmacy Shops</h2>
          <Button className={PharmacyStyle.addBtn} variant="contained" size="small" startIcon={<AddShoppingCartIcon />} onClick={() => { setOpen(true); setOperationMode('Add'); }}>
            Add Shop
          </Button>
        </div>
        <CommonAgGrid
          rowData={shops}
          columnDefs={columns}
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={Number(totalCount) || 0}
          pageSizeOptions={[5, 10, 25, 50]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          getRowId={(e) => String(e.data?._id)}
          height={280}
        />
        <PharmacyDialog open={open} setOpen={setOpen} editData={editData} operationMode={operationMode} setOperationMode={setOperationMode} callApi={callApi} />
      </Card>
    </div>
  );
}